import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../services/api';
import { useToast } from '../components/shared/ToastNotification';

export default function OrderInstallationManagement({ orderIdProp, inModal = false, onSuccess }: { orderIdProp?: string, inModal?: boolean, onSuccess?: () => void }) {
    const params = useParams();
    const id = orderIdProp || params.id;
    const toast = useToast();
    const [order, setOrder] = useState<any>(null);
    const [installations, setInstallations] = useState<InstallationRecord[]>([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingInstallation, setEditingInstallation] = useState<InstallationRecord | null>(null);
    const [formData, setFormData] = useState({
        product_unit_id: '',
        installation_status: 'pending',
        scheduled_date: '',
        scheduled_time: '',
        technician_name: '',
        technician_phone: '',
        installation_charge: '',
        installation_notes: '',
        completion_date: ''
    });

    useEffect(() => {
        loadData();
    }, [id]);

    // Handle direct add for modal view
    useEffect(() => {
        if (inModal && installations.length === 0 && !showForm && !editingInstallation) {
            handleAdd();
        }
    }, [inModal, installations.length, showForm, editingInstallation]);

    const loadData = async () => {
        try {
            const [orderRes, installationsRes] = await Promise.all([
                api.get(`/orders/${id}`),
                api.get(`/orders/${id}/installations`)
            ]);
            setOrder(orderRes.data);
            const installationsData = Array.isArray(installationsRes.data) ? installationsRes.data : (installationsRes.data.data || []);
            setInstallations(installationsData);
        } catch (error) {
            console.error('Error loading data:', error);
            toast.error('Failed to load installation data');
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setShowForm(false);
        setEditingInstallation(null);
        setFormData({
            product_unit_id: '',
            installation_status: 'pending',
            scheduled_date: '',
            scheduled_time: '',
            technician_name: '',
            technician_phone: '',
            installation_charge: '',
            installation_notes: '',
            completion_date: ''
        });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            let response;
            if (editingInstallation) {
                response = await api.put(`/orders/${id}/installations/${editingInstallation.id}`, { installation: formData });
            } else {
                response = await api.post(`/orders/${id}/installations`, formData);
            }

            if (response.success) {
                toast.success(`Installation ${editingInstallation ? 'updated' : 'scheduled'} successfully`);
                loadData();
                resetForm();
                if (onSuccess) onSuccess();
            } else {
                const msg = response.errors?.length > 0 ? response.errors[0] : (response.message || response.error || 'Error saving installation record');
                toast.error(msg);
            }
        } catch (error: any) {
            console.error('Error saving installation:', error);
            toast.error(error.message || 'An unexpected error occurred');
        }
    };

    const handleEdit = (installation: InstallationRecord) => {
        setEditingInstallation(installation);
        setFormData({
            product_unit_id: installation.product_unit_id,
            installation_status: installation.installation_status,
            scheduled_date: installation.scheduled_date || '',
            scheduled_time: '',
            technician_name: installation.technician_name || '',
            technician_phone: installation.technician_phone || '',
            installation_charge: installation.installation_charge?.toString() || '',
            installation_notes: '',
            completion_date: installation.completion_date || ''
        });
        setShowForm(true);
    };

    const handleAdd = () => {
        setEditingInstallation(null);
        setFormData({
            product_unit_id: order?.items?.length === 1 ? (order.items[0].product_unit_id || order.items[0].id) : '',
            installation_status: 'pending',
            scheduled_date: '',
            scheduled_time: '',
            technician_name: '',
            technician_phone: '',
            installation_charge: '',
            installation_notes: '',
            completion_date: ''
        });
        setShowForm(true);
    };

    const handleDelete = async (installationId: string) => {
        if (!confirm('Are you sure you want to delete this installation record?')) return;
        try {
            await api.delete(`/orders/${id}/installations/${installationId}`);
            loadData();
            if (onSuccess) onSuccess();
        } catch (error) {
            console.error('Error deleting installation:', error);
        }
    };


    const getStatusBadge = (status: string) => {
        const badges: Record<string, string> = {
            pending: 'bg-gray-100 text-gray-700',
            scheduled: 'bg-blue-100 text-blue-700',
            in_progress: 'bg-amber-100 text-amber-700',
            completed: 'bg-green-100 text-green-700',
            cancelled: 'bg-red-100 text-red-700'
        };
        return badges[status] || badges.pending;
    };

    if (loading) return <div className="p-8">Loading...</div>;
    if (!order) return <div className="p-8">Order not found.</div>;

    return (
        <div className="p-8 max-w-6xl mx-auto">
            <div className="mb-8">
                {!inModal && (
                    <Link to={`/orders/${id}`} className="inline-flex items-center gap-2 text-indigo-600 hover:text-indigo-800 font-bold mb-4">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                        </svg>
                        Back to Order
                    </Link>
                )}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className={`${inModal ? 'text-2xl' : 'text-3xl'} font-black text-slate-900`}>Installation Management</h1>
                        {!inModal && <p className="text-slate-600 mt-2">Order #{order.order_number}</p>}
                    </div>
                    <button
                        onClick={handleAdd}
                        className="px-6 py-3 bg-emerald-600 text-white rounded-xl font-bold hover:bg-emerald-700 transition-all text-sm"
                    >
                        + Schedule Installation
                    </button>
                </div>
            </div>

            {/* Installations Table */}
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden mb-8">
                <table className="w-full">
                    <thead className="bg-slate-50 border-b border-slate-200">
                        <tr>
                            <th className="px-6 py-4 text-left text-xs font-bold text-slate-600 uppercase">Product</th>
                            <th className="px-6 py-4 text-left text-xs font-bold text-slate-600 uppercase">Status</th>
                            <th className="px-6 py-4 text-left text-xs font-bold text-slate-600 uppercase">Scheduled Date</th>
                            <th className="px-6 py-4 text-left text-xs font-bold text-slate-600 uppercase">Technician</th>
                            <th className="px-6 py-4 text-right text-xs font-bold text-slate-600 uppercase">Charges</th>
                            <th className="px-6 py-4 text-right text-xs font-bold text-slate-600 uppercase">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {installations.length === 0 ? (
                            <tr>
                                <td colSpan={6} className="px-6 py-12 text-center text-slate-500">
                                    No installation records found. Click "Schedule Installation" to create one.
                                </td>
                            </tr>
                        ) : (
                            installations.map((inst) => (
                                <tr key={inst.id} className="hover:bg-slate-50">
                                    <td className="px-6 py-4 font-medium text-slate-900">{inst.product_name || 'Unknown'}</td>
                                    <td className="px-6 py-4">
                                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${getStatusBadge(inst.installation_status)}`}>
                                            {inst.installation_status.replace('_', ' ')}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-slate-700">{inst.scheduled_date || '-'}</td>
                                    <td className="px-6 py-4 text-slate-700">{inst.technician_name || '-'}</td>
                                    <td className="px-6 py-4 text-right font-mono text-slate-900">
                                        {inst.installation_charge ? `₹${inst.installation_charge.toLocaleString()}` : '-'}
                                    </td>
                                    <td className="px-6 py-4 text-right">
                                        <button
                                            onClick={() => handleEdit(inst)}
                                            className="text-indigo-600 hover:text-indigo-800 font-bold mr-4"
                                        >
                                            Edit
                                        </button>
                                        <button
                                            onClick={() => handleDelete(inst.id)}
                                            className="text-red-600 hover:text-red-800 font-bold"
                                        >
                                            Delete
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Form Section */}
            {showForm && (
                <div className={inModal ? "mb-8 bg-slate-50 rounded-2xl p-8 border border-slate-200 shadow-inner" : "fixed inset-0 bg-black/50 flex items-center justify-center z-50"}>
                    <div className={inModal ? "" : "bg-white rounded-2xl p-8 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto"}>
                        <h2 className="text-2xl font-black text-slate-900 mb-6">
                            {editingInstallation ? 'Edit Installation' : 'Schedule Installation'}
                        </h2>
                        <form onSubmit={handleSubmit} className="space-y-6">
                            {!editingInstallation && (
                                <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-2">Product Unit</label>
                                    <select
                                        required
                                        value={formData.product_unit_id}
                                        onChange={(e) => setFormData({ ...formData, product_unit_id: e.target.value })}
                                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                                    >
                                        <option value="">Select Product</option>
                                        {order.items?.map((item: any) => (
                                            <option key={item.id} value={item.product_unit_id || item.id}>
                                                {item.requires_installation ? '🔧 ' : ''}{item.product_name} - {item.product_unit_identifier || 'No SN'}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-2">Installation Status</label>
                                <select
                                    value={formData.installation_status}
                                    onChange={(e) => setFormData({ ...formData, installation_status: e.target.value })}
                                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                                >
                                    <option value="pending">Pending</option>
                                    <option value="scheduled">Scheduled</option>
                                    <option value="in_progress">In Progress</option>
                                    <option value="completed">Completed</option>
                                    <option value="cancelled">Cancelled</option>
                                </select>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-2">Scheduled Date</label>
                                    <input
                                        type="date"
                                        value={formData.scheduled_date}
                                        onChange={(e) => setFormData({ ...formData, scheduled_date: e.target.value })}
                                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-2">Completion Date</label>
                                    <input
                                        type="date"
                                        value={formData.completion_date}
                                        onChange={(e) => setFormData({ ...formData, completion_date: e.target.value })}
                                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-2">Technician Name</label>
                                    <input
                                        type="text"
                                        value={formData.technician_name}
                                        onChange={(e) => setFormData({ ...formData, technician_name: e.target.value })}
                                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                                        placeholder="John Doe"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-2">Technician Phone</label>
                                    <input
                                        type="tel"
                                        value={formData.technician_phone}
                                        onChange={(e) => setFormData({ ...formData, technician_phone: e.target.value })}
                                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                                        placeholder="+91 98765 43210"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-2">Installation Charges</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    value={formData.installation_charge}
                                    onChange={(e) => setFormData({ ...formData, installation_charge: e.target.value })}
                                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                                    placeholder="0.00"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-2">Installation Notes</label>
                                <textarea
                                    rows={3}
                                    value={formData.installation_notes}
                                    onChange={(e) => setFormData({ ...formData, installation_notes: e.target.value })}
                                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                                    placeholder="Any special instructions..."
                                />
                            </div>

                            <div className="flex gap-4 pt-4">
                                <button
                                    type="submit"
                                    className="flex-1 px-6 py-3 bg-emerald-600 text-white rounded-xl font-bold hover:bg-emerald-700 transition-all"
                                >
                                    {editingInstallation ? 'Update' : 'Schedule'} Installation
                                </button>
                                <button
                                    type="button"
                                    onClick={resetForm}
                                    className="px-6 py-3 bg-slate-200 text-slate-700 rounded-xl font-bold hover:bg-slate-300 transition-all"
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
